import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Tag, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ImageUpload from '../components/Common/ImageUpload';
import { uploadEquipmentImage, deleteUploadedFile } from '../services/uploadService';
import equipmentsService, { Equipment } from '../services/equipmentsService';
import { Customer, customersService } from '../services/customersService';

const statusColors: Record<string, string> = {
  active: 'green',
  inactive: 'default',
  maintenance: 'orange',
  retired: 'red',
};

const EquipmentsPage: React.FC = () => {
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [showOtherBrand, setShowOtherBrand] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | undefined>(undefined);
  const [form] = Form.useForm();

  const brandOptions = ['CARRIER', 'LK', 'TOSHIBA', 'AUTRES'];

  useEffect(() => {
    fetchEquipments();
    fetchCustomers();
  }, []);

  const fetchEquipments = async () => {
    setLoading(true);
    try {
      const response = await equipmentsService.getAll();
      setEquipments(response.data.data.equipments);
    } catch (e) {
      message.error('Erreur lors du chargement des équipements');
      console.error('Erreur API équipements:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await customersService.getCustomers({ limit: 100 });
      setCustomers(response.data.customers);
    } catch (e) {
      setCustomers([]);
    }
  };

  const openModal = (equipment?: Equipment) => {
    setEditMode(!!equipment);
    setEditingEquipment(equipment || null);
    if (equipment) {
      form.setFieldsValue(equipment);
      setCurrentImage((equipment as any).image_url);
      // Si la marque n'est pas dans la liste prédéfinie, c'est "AUTRES"
      if (equipment.brand && !brandOptions.slice(0, 3).includes(equipment.brand.toUpperCase())) {
        form.setFieldsValue({ brand: 'AUTRES', other_brand: equipment.brand });
        setShowOtherBrand(true);
      } else {
        setShowOtherBrand(equipment.brand === 'AUTRES');
      }
    } else {
      form.resetFields();
      setCurrentImage(undefined);
      setShowOtherBrand(false);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingEquipment(null);
    setShowOtherBrand(false);
    setCurrentImage(undefined);
    form.resetFields();
  };

  const handleImageUpload = async (file: File) => {
    if (!editingEquipment?.id) {
      message.warning('Veuillez d\'abord créer l\'équipement avant d\'ajouter une image');
      throw new Error('Equipment ID required');
    }
    
    const result = await uploadEquipmentImage(file, editingEquipment.id);
    setCurrentImage(result.url);
    message.success('Image uploadée avec succès');
    return result;
  };

  const handleImageDelete = async () => {
    if (currentImage) {
      const filename = currentImage.split('/').pop();
      if (filename) {
        await deleteUploadedFile('equipments', filename);
        setCurrentImage(undefined);
        message.success('Image supprimée');
      }
    }
  };

  const handleBrandChange = (value: string) => {
    setShowOtherBrand(value === 'AUTRES');
    if (value !== 'AUTRES') {
      form.setFieldsValue({ other_brand: undefined });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Si "AUTRES" est sélectionné, utiliser la valeur du champ "other_brand"
      if (values.brand === 'AUTRES' && values.other_brand) {
        values.brand = values.other_brand;
      }
      delete values.other_brand; // Supprimer le champ temporaire
      
      // Ajouter l'image si présente
      if (currentImage) {
        values.image_url = currentImage;
      }
      
      if (editMode && editingEquipment) {
        await equipmentsService.update(editingEquipment.id, values);
        message.success('Équipement modifié');
      } else {
        await equipmentsService.create(values);
        message.success('Équipement ajouté');
      }
      closeModal();
      fetchEquipments();
    } catch (e) {
      message.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await equipmentsService.remove(id);
      message.success('Équipement supprimé');
      fetchEquipments();
    } catch (e) {
      message.error('Erreur lors de la suppression');
    }
  };

  const columns = [
    { title: 'Nom', dataIndex: 'name', key: 'name' },
    { title: 'Type', dataIndex: 'type', key: 'type' },
    { title: 'Marque', dataIndex: 'brand', key: 'brand' },
    { title: 'Modèle', dataIndex: 'model', key: 'model' },
    { title: 'Client', dataIndex: ['customer'], key: 'customer', render: (customer: any) => customer?.customerProfile ? `${customer.customerProfile.first_name} ${customer.customerProfile.last_name}` : '-' },
    { title: 'Emplacement', dataIndex: 'location', key: 'location' },
    { title: 'Statut', dataIndex: 'status', key: 'status', render: (status: string) => <Tag color={statusColors[status] || 'default'}>{status}</Tag> },
    { title: 'Actions', key: 'actions', render: (_: any, record: Equipment) => (
      <Space>
        <Button icon={<EditOutlined />} onClick={() => openModal(record)} />
        <Popconfirm title="Supprimer cet équipement ?" onConfirm={() => handleDelete(record.id)} okText="Oui" cancelText="Non">
          <Button icon={<DeleteOutlined />} danger />
        </Popconfirm>
      </Space>
    ) },
  ];

  return (
    <div>
      <h2>Équipements</h2>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()} style={{ marginBottom: 16 }}>Ajouter un équipement</Button>
      <Table rowKey="id" columns={columns} dataSource={equipments} loading={loading} pagination={{ pageSize: 10 }} />
      <Modal
        open={modalOpen}
        title={editMode ? 'Modifier un équipement' : 'Ajouter un équipement'}
        onCancel={closeModal}
        onOk={handleSubmit}
        okText={editMode ? 'Enregistrer' : 'Ajouter'}
      >
        <Form form={form} layout="vertical" validateTrigger="onSubmit">
          <Form.Item name="name" label="Nom" rules={[{ required: true, message: 'Le nom est requis' }]}>
            <Input placeholder="Ex: Climatiseur Salon" />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Le type est requis' }]}>
            <Input placeholder="Ex: climatiseur, chauffe-eau, pompe" />
          </Form.Item>
          <Form.Item name="brand" label="Marque">
            <Select placeholder="Sélectionner une marque" onChange={handleBrandChange}>
              {brandOptions.map(brand => (
                <Select.Option key={brand} value={brand}>{brand}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          {showOtherBrand && (
            <Form.Item 
              name="other_brand" 
              label="Préciser la marque" 
              rules={[{ required: true, message: 'Veuillez préciser la marque' }]}
            >
              <Input placeholder="Ex: Samsung, Daikin, etc." />
            </Form.Item>
          )}
          <Form.Item name="model" label="Modèle">
            <Input placeholder="Ex: AR12, Dual Cool" />
          </Form.Item>
          <Form.Item name="customer_id" label="Client" rules={[{ required: true, message: 'Le client est requis' }]}>
            <Select showSearch optionFilterProp="children" placeholder="Sélectionner un client">
              {customers.map(c => <Select.Option key={c.user_id} value={c.user_id}>{c.first_name} {c.last_name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="location" label="Emplacement">
            <Input placeholder="Ex: Salon, Chambre 1" />
          </Form.Item>
          <Form.Item name="status" label="Statut" initialValue="active">
            <Select>
              {['active','inactive','maintenance','retired'].map(s => <Select.Option key={s} value={s}>{s}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Notes sur l'équipement" />
          </Form.Item>
          
          <Form.Item label="Image de l'équipement">
            <ImageUpload
              currentImage={currentImage}
              onUpload={handleImageUpload}
              onDelete={handleImageDelete}
              label="Photo de l'équipement"
              type="equipment"
              disabled={!editMode || !editingEquipment}
            />
            {!editMode && (
              <p style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                💡 Créez d'abord l'équipement, puis vous pourrez ajouter une photo
              </p>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EquipmentsPage;